export type AiRequestKind = 'summary' | 'transcription';
export type SummaryPeriod = '24h' | '3d' | '1w' | '2w' | '3w' | '1m';
export type AiInput =
    | {readonly kind: 'summary'; readonly period: SummaryPeriod}
    | {readonly kind: 'transcription'; readonly audioEventId: string};
export interface AiRequestMetadata {
    readonly schema_version: 1;
    readonly request_id: string;
    readonly timezone: string;
    readonly retry_of?: string;
}
export interface AiRequestContent {
    readonly [key: string]: unknown;
    readonly msgtype: string;
    readonly body: string;
    readonly 'org.phonehq.ai_request': AiRequestMetadata;
}
export const newAiRequestMetadata = (retryOf?: string): AiRequestMetadata => ({
    schema_version: 1,
    request_id: crypto.randomUUID(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    ...(retryOf ? {retry_of: retryOf} : {}),
});
export const buildAiRequest = (input: AiInput, metadata: AiRequestMetadata): AiRequestContent => ({
    msgtype: `org.phonehq.${input.kind}_request`,
    body: input.kind === 'summary' ? `Requesting summary for ${input.period}...` : 'Requesting transcript...',
    'org.phonehq.ai_request': metadata,
    ...(input.kind === 'summary' ? {time_range: input.period} : {
        audio_event_id: input.audioEventId,
        'm.relates_to': {rel_type: 'm.reference', event_id: input.audioEventId},
    }),
});
export const buildAiStatusRequest = (kind: AiRequestKind, requestEventId: string): AiRequestContent => ({
    msgtype: `org.phonehq.${kind}_status_request`,
    body: 'Checking AI request status...',
    'org.phonehq.ai_request': newAiRequestMetadata(),
    'm.relates_to': {rel_type: 'm.reference', event_id: requestEventId},
});

export interface AiTimelineEvent {
    getId(): string | undefined;
    getSender(): string | undefined;
    getContent(): unknown;
    getTs(): number;
    isRedacted(): boolean;
}
export const record = (value: unknown): value is Readonly<Record<string, unknown>> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
const isUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value)
    && value !== '00000000-0000-0000-0000-000000000000';
export const requestReference = (content: unknown): string | undefined => {
    if (!record(content) || !record(content['m.relates_to'])) return undefined;
    const relation = content['m.relates_to'];
    return typeof relation.event_id === 'string' ? relation.event_id : undefined;
};
const states = ['accepted', 'preparing', 'running', 'awaiting_delivery', 'unknown', 'published',
    'failed', 'cancelled', 'empty', 'no_speech'] as const;
export type AiState = typeof states[number];
const isState = (value: unknown): value is AiState => states.some(state => state === value);
export interface AiStatus {
    readonly id: string;
    readonly revision: number;
    readonly state: AiState;
    readonly retryAllowed: boolean;
    readonly errorCode?: string;
    readonly operationId?: string;
    readonly attemptId?: string;
}
export const readAiStatus = (content: unknown, kind: AiRequestKind): AiStatus | undefined => {
    if (!record(content) || content.msgtype !== `org.phonehq.${kind}_status`) return undefined;
    const status = content['org.phonehq.ai_status'];
    if (!record(status) || status.schema_version !== 1 || !isUuid(status.status_id)
        || typeof status.revision !== 'number' || !Number.isSafeInteger(status.revision) || status.revision < 1
        || !isState(status.state) || typeof status.retry_allowed !== 'boolean') return undefined;
    const hasIdentity = isUuid(status.operation_id) && isUuid(status.attempt_id);
    if (!hasIdentity && (status.state !== 'failed' || status.operation_id !== undefined || status.attempt_id !== undefined)) return undefined;
    if (status.retry_allowed && (!hasIdentity || status.state !== 'failed')) return undefined;
    return {
        id: status.status_id, revision: status.revision, state: status.state,
        retryAllowed: status.retry_allowed,
        ...(typeof status.error_code === 'string' ? {errorCode: status.error_code} : {}),
        ...(hasIdentity ? {operationId: String(status.operation_id), attemptId: String(status.attempt_id)} : {}),
    };
};
export const latestAiStatus = (options: {
    readonly events: readonly AiTimelineEvent[];
    readonly botUserId: string;
    readonly kind: AiRequestKind;
    readonly requestEventId: string;
}): AiStatus | undefined => {
    let latest: AiStatus | undefined;
    const seen = new Set<string>();
    for (const event of options.events) {
        if (event.isRedacted() || event.getSender() !== options.botUserId
            || requestReference(event.getContent()) !== options.requestEventId) continue;
        const status = readAiStatus(event.getContent(), options.kind);
        if (!status || seen.has(status.id)) continue;
        seen.add(status.id);
        if (!latest || status.revision > latest.revision) latest = status;
    }
    return latest;
};
export const isAiTerminal = (state: AiState): boolean =>
    ['published', 'failed', 'cancelled', 'empty', 'no_speech'].includes(state);
const errors: Readonly<Record<string, string>> = {
    forbidden: 'You are not allowed to use AI in this conversation.',
    feature_disabled: 'AI is not enabled for your account.',
    bot_absent: 'The AI bot has left this conversation.',
    membership_unknown: 'The bot’s participation history could not be verified.',
    history_incomplete: 'Some eligible conversation history is unavailable.',
    keys_unavailable: 'The bot cannot decrypt some eligible messages.',
    media_unavailable: 'The audio is unavailable.',
    unsupported_audio: 'This audio format or size is not supported.',
    input_changed: 'The source message or a prepared result was removed or changed.',
    request_conflict: 'This request conflicts with existing work.',
    provider_rejected: 'The AI provider could not complete this request.',
    invalid_output: 'The AI response could not be validated.',
    result_not_retained: 'The temporary result is no longer available. Retry to generate it again.',
    outcome_unknown: 'The provider outcome could not be confirmed.',
    context_limit: 'Too many messages to summarize at once. Choose a shorter period.',
    delivery_failed: 'The prepared result could not be delivered.',
};
export const aiStatusText = (status: AiStatus): string => {
    switch (status.state) {
        case 'accepted': return 'Request accepted.';
        case 'preparing': return 'Preparing eligible messages...';
        case 'running': return 'Processing your request...';
        case 'awaiting_delivery': return 'The result is ready and waiting for delivery.';
        case 'unknown': return 'Checking the existing request. No new AI attempt has started.';
        case 'published': return 'The result is ready.';
        case 'failed': return errors[status.errorCode ?? ''] ?? 'The request could not be completed.';
        case 'cancelled': return 'The request was cancelled.';
        case 'empty': return 'There are no eligible messages in this period. No charge.';
        case 'no_speech': return 'No speech was detected. No charge.';
    }
};
export interface LoadedAiRequest {
    readonly eventId: string;
    readonly content: AiRequestContent;
    readonly input: AiInput;
}
export const readLoadedAiRequest = (event: AiTimelineEvent): LoadedAiRequest | undefined => {
    const content = event.getContent();
    const eventId = event.getId();
    if (!eventId?.startsWith('$') || event.isRedacted() || !record(content)) return undefined;
    let input: AiInput;
    if (content.msgtype === 'org.phonehq.transcription_request' && typeof content.audio_event_id === 'string') {
        input = {kind: 'transcription', audioEventId: content.audio_event_id};
    } else if (content.msgtype === 'org.phonehq.summary_request' && isSummaryPeriod(content.time_range)) {
        input = {kind: 'summary', period: content.time_range};
    } else return undefined;
    const value = content['org.phonehq.ai_request'];
    if (!record(value) || value.schema_version !== 1 || !isUuid(value.request_id) || typeof value.timezone !== 'string'
        || (value.retry_of !== undefined && typeof value.retry_of !== 'string')) return undefined;
    const metadata: AiRequestMetadata = {
        schema_version: 1, request_id: value.request_id, timezone: value.timezone,
        ...(typeof value.retry_of === 'string' ? {retry_of: value.retry_of} : {}),
    };
    return {eventId, content: buildAiRequest(input, metadata), input};
};
export const isSummaryPeriod = (value: unknown): value is SummaryPeriod =>
    ['24h', '3d', '1w', '2w', '3w', '1m'].some(period => period === value);

export const resultBelongsToCurrentRequest = (options: {
    readonly result: AiTimelineEvent;
    readonly events: readonly AiTimelineEvent[];
    readonly currentUserId: string;
    readonly kind: AiRequestKind;
}): boolean => {
    if (options.result.isRedacted()) return false;
    const reference = requestReference(options.result.getContent());
    const request = options.events.find(event => event.getId() === reference && !event.isRedacted());
    const content = request?.getContent();
    return request?.getSender() === options.currentUserId && record(content)
        && content.msgtype === `org.phonehq.${options.kind}_request`;
};

export const summaryVisibleTo = (options: {
    readonly request: AiTimelineEvent | undefined;
    readonly fallbackRequesterId: string | undefined;
    readonly currentUserId: string;
}): boolean => options.request
    ? options.request.getSender() === options.currentUserId
    : !options.fallbackRequesterId || options.fallbackRequesterId === options.currentUserId;
